class ImageTextCarousel extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
    this.loopCheckInterval = null;
  }
  
  connectedCallback() {
    // Wait for slider component to be ready
    setTimeout(() => {
      this.sliderComponent = this.querySelector('slider-component');
      this.dots = this.querySelectorAll('.slider-dot');
      
      if (!this.sliderComponent || this.dots.length === 0 || this.initialized) return;
      
      this.init();
      this.initialized = true;
    }, 100);
  }
  
  disconnectedCallback() {
    // Clean up interval when component is removed
    if (this.loopCheckInterval) {
      clearInterval(this.loopCheckInterval);
    }
  }
  
  init() {
    // Add event listeners to dots
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index + 1);
      });
    });
    
    // Listen for slide changes from the slider component
    this.sliderComponent.addEventListener('slideChanged', (event) => {
      this.updateActiveDot(event.detail.currentPage);
    });
    
    // Force infinite loop with multiple approaches
    this.setupInfiniteLoop();
    this.setupButtonWatcher();
  }
  
  setupInfiniteLoop() {
    // Approach 1: Intercept clicks on next button
    if (this.sliderComponent.nextButton) {
      // Remove any existing event listeners by cloning the button
      const newNextButton = this.sliderComponent.nextButton.cloneNode(true);
      this.sliderComponent.nextButton.parentNode.replaceChild(newNextButton, this.sliderComponent.nextButton);
      this.sliderComponent.nextButton = newNextButton;
      
      // Add our custom event listener
      this.sliderComponent.nextButton.addEventListener('click', (event) => {
        const totalSlides = this.sliderComponent.sliderItemsToShow.length;
        const currentPage = this.getCurrentPage();
        
        if (currentPage >= totalSlides) {
          event.preventDefault();
          event.stopPropagation();
          this.goToFirstSlide();
          return false;
        }
        
        // Allow normal next slide behavior
        this.goToNextSlide();
      });
    }
    
    // Approach 2: Also handle previous button for complete loop
    if (this.sliderComponent.previousButton) {
      const newPrevButton = this.sliderComponent.previousButton.cloneNode(true);
      this.sliderComponent.previousButton.parentNode.replaceChild(newPrevButton, this.sliderComponent.previousButton);
      this.sliderComponent.previousButton = newPrevButton;
      
      this.sliderComponent.previousButton.addEventListener('click', (event) => {
        const currentPage = this.getCurrentPage();
        
        if (currentPage <= 1) {
          event.preventDefault();
          event.stopPropagation();
          this.goToLastSlide();
          return false;
        }
        
        this.goPreviousSlide();
      });
    }
  }
  
  setupButtonWatcher() {
    // Watch for button state changes and force enable
    this.loopCheckInterval = setInterval(() => {
      if (this.sliderComponent.nextButton) {
        // Always keep next button enabled
        this.sliderComponent.nextButton.disabled = false;
        this.sliderComponent.nextButton.classList.remove('disabled');
        this.sliderComponent.nextButton.style.pointerEvents = 'auto';
        this.sliderComponent.nextButton.style.opacity = '1';
      }
      
      if (this.sliderComponent.previousButton) {
        // Always keep previous button enabled
        this.sliderComponent.previousButton.disabled = false;
        this.sliderComponent.previousButton.classList.remove('disabled');
        this.sliderComponent.previousButton.style.pointerEvents = 'auto';
        this.sliderComponent.previousButton.style.opacity = '1';
      }
    }, 100);
  }
  
  getCurrentPage() {
    // Try multiple ways to get current page
    if (this.sliderComponent.currentPage) {
      return this.sliderComponent.currentPage;
    }
    
    // Try to calculate from scroll position
    const container = this.sliderComponent.slider || this.sliderComponent.querySelector('.slider');
    if (container && this.sliderComponent.sliderItemsToShow.length > 0) {
      const slideWidth = this.sliderComponent.sliderItemsToShow[0].offsetWidth;
      if (slideWidth > 0) {
        return Math.round(container.scrollLeft / slideWidth) + 1;
      }
    }
    
    // Try to find active slide
    const activeSlide = this.sliderComponent.querySelector('.slider-item.active, .slide.active, [aria-current="true"]');
    if (activeSlide) {
      const slides = Array.from(this.sliderComponent.sliderItemsToShow);
      return slides.indexOf(activeSlide) + 1;
    }
    
    return 1; // Default to first slide
  }
  
  goToSlide(slideNumber) {
    if (!this.sliderComponent.sliderItemsToShow || slideNumber < 1) return;
    
    const totalSlides = this.sliderComponent.sliderItemsToShow.length;
    const targetSlideIndex = Math.min(slideNumber, totalSlides) - 1;
    const targetSlide = this.sliderComponent.sliderItemsToShow[targetSlideIndex];
    
    if (!targetSlide) return;
    
    // Calculate scroll position
    const slideScrollPosition = targetSlide.offsetLeft - this.sliderComponent.sliderItemsToShow[0].offsetLeft;
    
    // Try multiple methods to set position
    if (this.sliderComponent.setSlidePosition) {
      this.sliderComponent.setSlidePosition(slideScrollPosition);
    }
    
    // Direct scroll method
    const container = this.sliderComponent.slider || this.sliderComponent.querySelector('.slider');
    if (container) {
      container.scrollLeft = slideScrollPosition;
    }
    
    // Update current page
    if (this.sliderComponent.currentPage !== undefined) {
      this.sliderComponent.currentPage = slideNumber;
    }
    
    // Trigger change event
    this.sliderComponent.dispatchEvent(new CustomEvent('slideChanged', {
      detail: { currentPage: slideNumber }
    }));
    
    // Update dots
    this.updateActiveDot(slideNumber);
  }
  
  goToFirstSlide() {
    this.goToSlide(1);
  }
  
  goToLastSlide() {
    const totalSlides = this.sliderComponent.sliderItemsToShow.length;
    this.goToSlide(totalSlides);
  }
  
  goToNextSlide() {
    const currentPage = this.getCurrentPage();
    const totalSlides = this.sliderComponent.sliderItemsToShow.length;
    
    if (currentPage >= totalSlides) {
      this.goToFirstSlide();
    } else {
      this.goToSlide(currentPage + 1);
    }
  }
  
  goPreviousSlide() {
    const currentPage = this.getCurrentPage();
    
    if (currentPage <= 1) {
      this.goToLastSlide();
    } else {
      this.goToSlide(currentPage - 1);
    }
  }
  
  updateActiveDot(currentPage) {
    if (!currentPage || this.dots.length === 0) return;
    
    // Remove active class from all dots
    this.dots.forEach(dot => {
      dot.classList.remove('slider-dot--active');
    });
    
    // Add active class to current dot (handle loop)
    const totalSlides = this.dots.length;
    const dotIndex = ((currentPage - 1) % totalSlides + totalSlides) % totalSlides;
    const currentDot = this.dots[dotIndex];
    
    if (currentDot) {
      currentDot.classList.add('slider-dot--active');
    }
  }
}

// Register the custom element
customElements.define('image-text-carousel', ImageTextCarousel);